// src/pages/offlineSales/customerDisplay/CustomerDisplayPage.jsx
// Customer-facing second screen. Read-only: everything arrives from the terminal over
// BroadcastChannel (see customerDisplayChannel.js) — no API calls.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Maximize, ShoppingBag } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getImageUrl } from '../../../utils/imageUtils';
import { getCustomerDisplayChannelName } from './customerDisplayChannel';
import styles from './CustomerDisplayPage.module.css';

const NO_IMAGE_SRC = '/assets/other/no-image-preview.png';

const handleNoImage = (e) => {
  if (!e.currentTarget.src.endsWith(NO_IMAGE_SRC)) {
    e.currentTarget.src = NO_IMAGE_SRC;
  }
};

const useClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
};

const CustomerDisplayPage = () => {
  const { shopId } = useParams();
  const { formatCurrency, formatNumber } = useLocalization();
  const [state, setState] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const now = useClock();

  const formatAmount = (amount) =>
    formatNumber(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(getCustomerDisplayChannelName(shopId));
    channel.onmessage = (e) => {
      if (e.data?.type === 'state') setState(e.data.state);
    };
    channel.postMessage({ type: 'hello' });
    return () => channel.close();
  }, [shopId]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    document.title = state?.shopName ? `${state.shopName} — Customer Display` : 'Customer Display';
  }, [state?.shopName]);

  const goFullscreen = () => {
    document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const mode = state?.mode || 'idle';

  return (
    <div className={styles.display}>
      {!isFullscreen && (
        <button className={styles.fullscreenHint} onClick={goFullscreen}>
          <Maximize size={16} /> Click to go full screen
        </button>
      )}

      {mode === 'idle' && (
        <div className={styles.idle}>
          <ShoppingBag size={72} className={styles.idleIcon} />
          <h1 className={styles.idleShop}>{state?.shopName || 'Welcome'}</h1>
          <p className={styles.idleWelcome}>Welcome! Your items will appear here.</p>
          <div className={styles.idleClock}>
            {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className={styles.idleDate}>
            {now.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
      )}

      {mode === 'paid' && state?.paid && (
        <div className={styles.paid}>
          <CheckCircle2 size={96} className={styles.paidIcon} />
          <h1 className={styles.paidTitle}>Thank you!</h1>
          <p className={styles.paidSub}>Please come again — {state.shopName}</p>
          <div className={styles.paidCard}>
            <div className={styles.paidRow}>
              <span>Total</span>
              <strong>{formatCurrency(state.paid.grandTotal)}</strong>
            </div>
            <div className={styles.paidRow}>
              <span>Paid</span>
              <strong>{formatCurrency(state.paid.paidAmount)}</strong>
            </div>
            {state.paid.changeAmount > 0 && (
              <div className={`${styles.paidRow} ${styles.paidChange}`}>
                <span>Change</span>
                <strong>{formatCurrency(state.paid.changeAmount)}</strong>
              </div>
            )}
            {state.paid.balanceAmount > 0 && (
              <div className={`${styles.paidRow} ${styles.paidBalance}`}>
                <span>Balance due</span>
                <strong>{formatCurrency(state.paid.balanceAmount)}</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {mode === 'cart' && (
        <div className={styles.cart}>
          <div className={styles.itemsPanel}>
            <div className={styles.itemsHeader}>
              <span>Item</span>
              <span className={styles.colQty}>Qty</span>
              <span className={styles.colPrice}>Price</span>
              <span className={styles.colTotal}>Total</span>
            </div>
            <div className={styles.itemsList}>
              {[...state.items].reverse().map(item => (
                <div key={item.key} className={styles.itemRow}>
                  <div className={styles.itemMain}>
                    <img
                      src={item.image ? getImageUrl(item.image) : NO_IMAGE_SRC}
                      alt=""
                      className={styles.itemThumb}
                      onError={handleNoImage}
                    />
                    <div className={styles.itemInfo}>
                      <span className={styles.itemName}>{item.name}</span>
                      {(item.variationLabel || item.lineDiscount > 0) && (
                        <span className={styles.itemMeta}>
                          {[
                            item.variationLabel,
                            item.lineDiscount > 0 && `Discount -${formatAmount(item.lineDiscount)}`,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={styles.colQty}>{item.quantity}</span>
                  <span className={styles.colPrice}>{formatAmount(item.unitPrice)}</span>
                  <span className={`${styles.colTotal} ${styles.itemTotal}`}>{formatAmount(item.lineTotal)}</span>
                </div>
              ))}
            </div>
          </div>

          <aside className={styles.totalsPanel}>
            <div className={styles.totalsShop}>{state.shopName}</div>
            {state.customerName && (
              <div className={styles.totalsCustomer}>Customer: {state.customerName}</div>
            )}
            <div className={styles.totalsRows}>
              <div className={styles.totalsRow}>
                <span>Items</span>
                <strong>{state.itemCount}</strong>
              </div>
              <div className={styles.totalsRow}>
                <span>Subtotal</span>
                <strong>{formatCurrency(state.subtotal)}</strong>
              </div>
              {state.totalDiscount > 0 && (
                <div className={`${styles.totalsRow} ${styles.totalsDiscount}`}>
                  <span>Discount</span>
                  <strong>-{formatCurrency(state.totalDiscount)}</strong>
                </div>
              )}
            </div>
            <div className={styles.grandTotal}>
              <span>Total to Pay</span>
              <strong>{formatCurrency(state.grandTotal)}</strong>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default CustomerDisplayPage;
